import { 
  collection, 
  doc, 
  getDocs, 
  deleteDoc, 
  setDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

interface SessionData {
  id: string;
  name: string;
  createdBy: string;
  createdAt: Timestamp;
  lastActivityAt: Timestamp;
  isActive: boolean;
  currentUsers: string[];
  maxUsers: number;
}

class SessionCleanupService {
  private cleanupInterval: NodeJS.Timeout | null = null;
  private readonly CLEANUP_INTERVAL = 60 * 60 * 1000; // 1 hour (check every hour)
  private readonly INACTIVITY_THRESHOLD = 4 * 60 * 60 * 1000; // 4 hours of inactivity
  private readonly EMPTY_SESSION_THRESHOLD = 2 * 60 * 60 * 1000; // 2 hours when empty
  private readonly HEARTBEAT_TIMEOUT = 40 * 60 * 1000; // 40 minutes - if no heartbeat, user is offline

  constructor() {
    this.startPeriodicCleanup();
  }

  /**
   * Start periodic cleanup of inactive sessions
   */
  private startPeriodicCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    this.cleanupInterval = setInterval(() => {
      this.cleanupInactiveSessions().catch(error => {
        console.error('Session cleanup error:', error);
      });
      this.cleanupInactiveUsers().catch(error => {
        console.error('Inactive user cleanup error:', error);
      });
    }, this.CLEANUP_INTERVAL);

  }

  /**
   * Stop periodic cleanup
   */
  public stopCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Clean up inactive sessions
   */
  public async cleanupInactiveSessions(): Promise<void> {
    try {
      
      const sessionsRef = collection(db, 'sessions');
      const now = new Date();
      const inactiveThreshold = new Date(now.getTime() - this.INACTIVITY_THRESHOLD);
      const emptyThreshold = new Date(now.getTime() - this.EMPTY_SESSION_THRESHOLD);

      // Get all sessions
      const sessionsSnapshot = await getDocs(sessionsRef);
      const sessionsToDelete: string[] = [];

      for (const sessionDoc of sessionsSnapshot.docs) {
        const sessionData = sessionDoc.data() as SessionData;
        const sessionId = sessionDoc.id;
        
        // Check if session should be deleted
        const shouldDelete = this.shouldDeleteSession(sessionData, inactiveThreshold, emptyThreshold);
        
        if (shouldDelete) {
          sessionsToDelete.push(sessionId);
        }
      }

      // Delete sessions in batches
      if (sessionsToDelete.length > 0) {
        
        const deletePromises = sessionsToDelete.map(sessionId => 
          this.deleteSessionAndRelatedData(sessionId)
        );
        
        await Promise.all(deletePromises);
      } else {
      }

    } catch (error) {
      console.error('Session cleanup failed:', error);
    }
  }

  /**
   * Determine if a session should be deleted
   */
  private shouldDeleteSession(
    sessionData: SessionData, 
    inactiveThreshold: Date, 
    emptyThreshold: Date
  ): boolean {
    const lastActivity = sessionData.lastActivityAt?.toDate() || sessionData.createdAt?.toDate();
    const isEmpty = sessionData.currentUsers.length === 0;
    const isNotActive = !sessionData.isActive;
    
    // Only delete if:
    // 1. Session is completely empty (no users) AND has been empty for 2+ hours
    // 2. OR session is marked inactive AND has been inactive for 4+ hours
    // 3. AND the session is not currently being used (double-check with currentUsers)
    const isEmptyForTooLong = isEmpty && lastActivity < emptyThreshold;
    const isInactiveForTooLong = isNotActive && lastActivity < inactiveThreshold;
    
    // Additional safety check: don't delete if session has users
    const hasActiveUsers = sessionData.currentUsers.length > 0;

    return (isEmptyForTooLong || isInactiveForTooLong) && !hasActiveUsers;
  }

  /**
   * Delete a session and all its related data
   */
  private async deleteSessionAndRelatedData(sessionId: string): Promise<void> {
    try {
      // Delete session document
      const sessionRef = doc(db, 'sessions', sessionId);
      await deleteDoc(sessionRef);

      // Delete related collections
      await this.deleteSessionSubcollections(sessionId);
      
      console.log(`🧹 Deleted session ${sessionId} and all related data`);
    } catch (error) {
      console.error(`Failed to delete session ${sessionId}:`, error);
    }
  }

  /**
   * Delete all subcollections of a session
   */
  private async deleteSessionSubcollections(sessionId: string): Promise<void> {
    const subcollections = ['messages', 'calculations', 'variables', 'session_users'];
    
    for (const subcollection of subcollections) {
      try {
        const subcollectionRef = collection(db, 'sessions', sessionId, subcollection);
        const snapshot = await getDocs(subcollectionRef);
        
        const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
        
      } catch (error) {
        console.error(`Failed to delete ${subcollection}:`, error);
      }
    }
  }


  /**
   * Clean up users who haven't sent heartbeats recently
   */
  public async cleanupInactiveUsers(): Promise<void> {
    try {
      console.log('🧹 Cleaning up inactive users...');
      
      const usersRef = collection(db, 'users');
      const now = new Date();
      const heartbeatTimeout = new Date(now.getTime() - this.HEARTBEAT_TIMEOUT);

      const usersSnapshot = await getDocs(usersRef);
      const inactiveUsers: string[] = [];

      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        const lastSeen = userData.lastSeenAt?.toDate();
        
        // If user is marked as online but hasn't sent heartbeat recently
        if (userData.isOnline && lastSeen && lastSeen < heartbeatTimeout) {
          inactiveUsers.push(userDoc.id);
        }
      }

      if (inactiveUsers.length > 0) {
        // Mark users as offline
        const updatePromises = inactiveUsers.map(userId => {
          const userRef = doc(db, 'users', userId);
          return setDoc(userRef, {
            isOnline: false,
            lastSeenAt: serverTimestamp()
          }, { merge: true });
        });
        
        await Promise.all(updatePromises);
      }

    } catch (error) {
      console.error('Inactive user cleanup failed:', error);
    }
  }

  /**
   * Clean up old user data
   */
  public async cleanupOldUsers(): Promise<void> {
    try {
      const usersRef = collection(db, 'users');
      const now = new Date();
      const oldUserThreshold = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000)); // 7 days

      const usersSnapshot = await getDocs(usersRef);
      const usersToDelete: string[] = [];

      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        const lastSeen = userData.lastSeenAt?.toDate();
        
        if (lastSeen && lastSeen < oldUserThreshold && !userData.isOnline) {
          usersToDelete.push(userDoc.id);
        }
      }

      if (usersToDelete.length > 0) {
        console.log(`🧹 Deleting ${usersToDelete.length} old users`);
        
        const deletePromises = usersToDelete.map(userId => 
          deleteDoc(doc(db, 'users', userId))
        );
        
        await Promise.all(deletePromises);
        console.log(`🧹 Successfully deleted ${usersToDelete.length} old users`);
      }

    } catch (error) {
      console.error('User cleanup failed:', error);
    }
  }
}

export const sessionCleanupService = new SessionCleanupService();
export default sessionCleanupService;
